import { Formik } from "formik";
import { useState, useEffect } from "react";
import { Alert, Button, Col, Form, Modal, Spinner } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import Multiselect from "../../../components/MultiselectFormik";
import * as Yup from "yup";
import moment from "moment";
import { saveConfSmart } from "../../../redux/evaluacion_desempenio/ConfSmart/actions";
import toastr from "toastr";
import messages from "../../../redux/messages";
import { FORM_TYPES } from "../../../pages/evaluacion_desempenio/utils/Constants";

const CreateConfModal = ({ showFormModal, setShowFormModal, listEmpleados, setEmpleadosList, data, setRecargar, recargar }) => {
    const [validated, setValidated] = useState(false);
    const dispatch = useDispatch();

    //Esquema de validación
    const schema = Yup.object().shape({
        formulario: Yup.array().test({
            // Mensaje ampliado para indicar la regla específica
            message: "**Campo requerido. Seleccione exactamente 2 formularios: uno debe ser 'Formulario por Objetivos' y el otro 'Formulario por Valores' o 'Formulario por Competencias'.",
            test: (val) => {
                if (!val || val.length !== 2) return false;
                const labels = val.map(v => v.label);
                const hasObjectives = labels.includes(FORM_TYPES.OBJECTIVES);
                const hasOther = labels.some(l => l === FORM_TYPES.VALUES || l === FORM_TYPES.COMPETENCIES);
                return hasObjectives && hasOther;
            },
        }),
        nombre: Yup.string().required("**Campo requerido"),
        descripcion: Yup.string().required("**Campo requerido"),
        fecha_lanzamiento: Yup.date()
            .required("**Campo requerido")
            .max(Yup.ref("fecha_revision"), "La fecha de lanzamiento no puede ser mayor que la de revisión"),
        fecha_revision: Yup.date()
            .required("**Campo requerido")
            .min(Yup.ref("fecha_lanzamiento"), "La fecha de revisión no puede ser menor que la de lanzamiento")
            .max(Yup.ref("fecha_llenado"), "La fecha de revisión no puede ser mayor que la de llenado"),
        fecha_llenado: Yup.date()
            .required("**Campo requerido")
            .min(Yup.ref("fecha_revision"), "La fecha de llenado no puede ser menor que la de revisión"),
    });

    const {
        loading: loadingFormType,
        formTypes,
        formTypeError
    } = useSelector((store) => store.formType);

    const {
        saveLoading,
        formError,
    } = useSelector((store) => store.confSmart);

    const formTypeOptions = formTypes?.map((formType) => ({
        value: formType.id,
        label: formType.nombre
    })) || [];

    useEffect(() => {
        if (!formError && showFormModal && recargar) {
            setShowFormModal(false);
            setEmpleadosList([]);
            toastr.success(messages.successMessage, "", {
                progressBar: true,
                rtl: true,
            });
        } else {
           
            setRecargar(false);
        }
    }, [recargar, formError]);


    const onSubmit = async (values) => {
        if (!window.confirm("¿Está seguro que desea crear la configuración?")) return;

        let formulariosId = values.formulario.map(form => form.value);

        //Preparar los datos para la api de revisión de empleados
        const empleadosRevision = listEmpleados.map(empleado => ({
            empleado: empleado.id
        }));

        //Dividir los empleados en la lista de empleados por .posicion para la api de creación de configuración
        const empleadosPorPosicion = listEmpleados.reduce((acc, empleado) => {
            const { posicion } = empleado;
            if (!acc[posicion]) {
                acc[posicion] = [];
            }
            acc[posicion].push({ empleado: empleado.id, formularios: formulariosId });
            return acc;
        }, {});
        await dispatch(saveConfSmart(values,data, empleadosPorPosicion,empleadosRevision, setShowFormModal));
        setRecargar(true);
    }

    return (
        <Modal show={showFormModal} onHide={() => setShowFormModal(false)}>
            <Formik
                validationSchema={schema}
                onSubmit={onSubmit}
                initialValues={{
                    formulario: [],
                    nombre: "",
                    descripcion: "",
                    fecha_lanzamiento: "",
                    fecha_revision: "",
                    fecha_llenado: "",
                }}
            >
                {({
                    handleSubmit,
                    handleChange,
                    handleBlur,
                    setFieldValue,
                    setFieldTouched,
                    values,
                    touched,
                    errors,
                }) => (
                    <Form onSubmit={handleSubmit} noValidate validated={validated}>
                        <Modal.Header closeButton>
                            <Modal.Title className="ft-modal-title">
                                Crear Configuración
                            </Modal.Title>
                        </Modal.Header>
                        <Modal.Body>
                            {formError &&
                                typeof formError !== "object" ? (
                                <Alert 
                                    variant="danger" 
                                    className="mt-4 mb-2"
                                    style={{
                                        maxHeight: '150px',
                                        overflowY: 'auto',
                                        wordBreak: 'break-word',
                                        fontSize: '0.9rem',
                                        lineHeight: '1.4'
                                    }}
                                >
                                    <div style={{ whiteSpace: 'pre-wrap' }}>
                                        {formError}
                                    </div>
                                </Alert>
                            ) : null}
                            <Form.Group as={Col} sm={12} md={12} lg={12} className="mb-3">
                                <Form.Label column sm="12" md="12">
                                    Nombre
                                </Form.Label>
                                <Form.Control
                                    type="text"
                                    name="nombre"
                                    className="rounded-pill"
                                    placeholder="Nombre"
                                    value={values.nombre}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    isValid={touched.nombre && !errors.nombre}
                                    isInvalid={touched.nombre && errors.nombre}
                                />
                            </Form.Group>
                            <Form.Group as={Col} sm={12} md={12} lg={12} className="mb-3">
                                <Form.Label column sm="12" md="12">
                                    Descripción
                                </Form.Label>
                                <Form.Control
                                    as="textarea"
                                    name="descripcion"
                                    placeholder="Descripción"
                                    value={values.descripcion}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    isValid={touched.descripcion && !errors.descripcion}
                                    isInvalid={touched.descripcion && errors.descripcion}
                                />
                            </Form.Group>
                            <Form.Group as={Col} sm={12} md={12} lg={12} className="mb-3">
                                <Form.Label column sm="12" md="12">
                                    Formulario
                                </Form.Label>
                                <Multiselect
                                    id="formulario"
                                    name="formulario"
                                    disabled={loadingFormType}
                                    options={formTypeOptions.map(opt => ({
                                        ...opt,
                                        disabled: values.formulario.length >= 2 && !values.formulario.some(f => f.id === opt.id)
                                    }))}
                                    value={values.formulario}
                                    onChange={(selected) => {
                                        if (selected.length <= 2) {
                                            setFieldValue(
                                                "formulario",
                                                selected.length > 0 ? selected : []
                                            );
                                        }
                                    }}
                                    onBlur={() => handleBlur("formulario")}
                                    onFocus={() => setFieldTouched("formulario", true)}
                                    maxSelectedOptions={2}
                                    disableSelectAll={true}
                                />
                                {touched.formulario && errors.formulario && (
                                    <small className="mx-2 text-danger">
                                        {errors.formulario}
                                    </small>
                                )}
                            </Form.Group>
                            <Form.Group as={Col} sm={12} md={12} lg={12} className="mb-3">
                                <Form.Label column sm="12" md="12">
                                    Fecha de lanzamiento
                                </Form.Label>
                                <Form.Control
                                    type="date"
                                    name="fecha_lanzamiento"
                                    value={values.fecha_lanzamiento}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    isValid={touched.fecha_lanzamiento && !errors.fecha_lanzamiento}
                                    isInvalid={touched.fecha_lanzamiento && errors.fecha_lanzamiento}
                                />
                                {touched.fecha_lanzamiento && errors.fecha_lanzamiento && (
                                    <small className="mx-2 text-danger">
                                        {errors.fecha_lanzamiento}
                                    </small>
                                )}
                            </Form.Group>
                            <Form.Group as={Col} sm={12} md={12} lg={12} className="mb-3">
                                <Form.Label column sm="12" md="12">
                                    Fecha de revisión
                                </Form.Label>
                                <Form.Control
                                    type="date"
                                    name="fecha_revision"
                                    value={values.fecha_revision}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    isValid={touched.fecha_revision && !errors.fecha_revision}
                                    isInvalid={touched.fecha_revision && errors.fecha_revision}
                                />
                                {touched.fecha_revision && errors.fecha_revision && (
                                    <small className="mx-2 text-danger">
                                        {errors.fecha_revision}
                                    </small>
                                )}
                            </Form.Group>
                            <Form.Group as={Col} sm={12} md={12} lg={12} className="mb-3">
                                <Form.Label column sm="12" md="12">
                                    Fecha de llenado
                                </Form.Label>
                                <Form.Control
                                    type="date"
                                    name="fecha_llenado"
                                    value={values.fecha_llenado}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    isValid={touched.fecha_llenado && !errors.fecha_llenado}
                                    isInvalid={touched.fecha_llenado && errors.fecha_llenado}
                                />
                                {touched.fecha_llenado && errors.fecha_llenado && (
                                    <small className="mx-2 text-danger">
                                        {errors.fecha_llenado}
                                    </small>
                                )}
                            </Form.Group>
                        </Modal.Body>
                        <Modal.Footer>
                            <Button
                                className="rounded-pill"
                                variant="secondary"
                                onClick={() => setShowFormModal(false)}
                            >
                                Cerrar
                            </Button>
                            <Button
                                className="rounded-pill"
                                disabled={saveLoading}
                                variant="primary"
                                type="submit"
                            >
                                {saveLoading && (
                                    <Spinner
                                        className="mr-2"
                                        as="span"
                                        animation="border"
                                        size="sm"
                                        role="status"
                                        aria-hidden="true"
                                    />
                                )}
                                Guardar
                            </Button>
                        </Modal.Footer>
                    </Form>
                )}
            </Formik>
        </Modal>


    )
}

export default CreateConfModal;